import { useState, useCallback, createContext, useContext } from 'react';
import Axios from 'axios';
import { Address } from '../entities/Address';

interface ApiResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro: boolean;
}

interface CEPContextData {
  getAddressData: (cep: string) => Promise<void>;
  setAddressData: React.Dispatch<React.SetStateAction<Address | undefined>>;
  addressData?: Address;
  loading: boolean;
  error?: string;
}

const CepContext = createContext<CEPContextData>({} as CEPContextData);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CepProvider: React.FC = ({ children }) => {
  const [addressData, setAddressData] = useState<Address>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const clearCep = useCallback((cep: string) => cep.replace(/\D/g, ''), []);

  const validateCep = useCallback((cep: string) => {
    const expression = /^[0-9]{8}$/;

    if (!cep) {
      throw new Error('CEP não informado.');
    }

    if (!expression.test(cep)) {
      throw new Error('CEP inválido.');
    }
  }, []);

  const checkCepData = useCallback((data: ApiResponse) => {
    if (data.erro) {
      throw new Error('CEP não encontrado.');
    }
  }, []);

  const getAddressData = useCallback(
    async (cep: string) => {
      const cepOnlyDigits = clearCep(cep);

      setLoading(true);
      setError(undefined);

      try {
        validateCep(cepOnlyDigits);

        const { data } = await Axios.get<ApiResponse>(
          `https://viacep.com.br/ws/${cepOnlyDigits}/json`,
        );

        checkCepData(data);

        setAddressData({
          cep: data.cep,
          number: '',
          street: data.logradouro,
          complement: data.complemento,
          neighborhood: data.bairro,
          city: data.localidade,
          uf: data.uf,
        });
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    },
    [clearCep, validateCep, checkCepData],
  );

  return (
    <CepContext.Provider
      value={{
        getAddressData,
        addressData,
        setAddressData,
        loading,
        error,
      }}
    >
      {children}
    </CepContext.Provider>
  );
};

export const useCep = (): CEPContextData => {
  const context = useContext(CepContext);

  if (!context) {
    throw new Error('useCep must be used within a cep provider');
  }

  return context;
};
